import { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { has, isArray } from 'lodash';

import api, { openApi } from '../../services/api';
import toastError from '../../errors/toastError';
import { unsubscribeAndUnregister } from '../../services/pushSubscriptionService';
import { socketConnection } from '../../services/socket';

const AUTH_SAFETY_TIMEOUT_MS = 8000; // garante que loading=false mesmo em rede travada

const USER_CACHE_KEY = 'cached_user_v1';

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};

const writeCachedUser = (u) => {
  try {
    if (u && u.id) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
  } catch (_) {
    // silent
  }
};

const clearCachedUser = () => {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch (_) {
    // silent
  }
};

/**
 * Resolve o array flat de permissions para quem respeita RBAC (todos exceto owner/super).
 * O backend pode ou não incluir `permissions` no /auth/me; se não incluir,
 * buscamos de /users/:id/permissions (role + overrides) para popular o campo.
 */
const resolvePermissions = async (userData) => {
  if (!userData?.id) return userData;
  if (userData.super || userData.isOwner) return userData;
  if (userData.permissions !== undefined && userData.permissions !== null) return userData;

  try {
    const { data } = await api.get(`/users/${userData.id}/permissions`);
    const perms = new Set(data.rolePermissions ?? []);
    for (const o of data.overrides ?? []) {
      if (o.granted) perms.add(o.permissionKey);
      else perms.delete(o.permissionKey);
    }
    return {
      ...userData,
      permissions: [...perms],
      ticketWhatsappIds: data.ticketWhatsappIds ?? [],
    };
  } catch {
    return userData;
  }
};

const useAuth = () => {
  const history = useHistory();
  const splashTimeoutRef = useRef(null);
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(() => readCachedUser());
  const [socket, setSocket] = useState(null);
  const [loginOrigin, setLoginOrigin] = useState(
    () => localStorage.getItem('loginOrigin') || 'default'
  );

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Tenta fazer parse se for string JSON
          const parsedToken = token.startsWith('"') ? JSON.parse(token) : token;
          config.headers['Authorization'] = `Bearer ${parsedToken}`;
          setIsAuth(true);
        } catch (e) {
          // Se falhar, usa o token direto
          config.headers['Authorization'] = `Bearer ${token}`;
          setIsAuth(true);
        }
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Bloqueio comercial: backend retorna 403 com código de motivo.
      // Propaga o erro normalmente — o PlanPermissionsContext e o layout
      // lêem user.company.status para exibir a tela de bloqueio adequada.
      // Não fazemos logout em 403 pois o usuário ainda está autenticado.
      if (error?.response?.status === 403) {
        return Promise.reject(error);
      }

      // 402: empresa inativa/trial vencido (checkCompanyActive). Leva direto
      // para a tela de bloqueio — sem isso, usuário com a aba aberta na virada
      // do dia receberia toasts de erro soltos em vez do fluxo de escolher plano.
      if (error?.response?.status === 402) {
        const path = window.location.pathname;
        if (path !== '/account-blocked' && !path.startsWith('/subscription')) {
          window.location.assign('/account-blocked');
        }
        return Promise.reject(error);
      }

      // Fluxo de refresh conforme doc: em 401 tentar refresh, retry, ou logout
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          doLogout();
          return Promise.reject(error);
        }

        try {
          const { data } = await openApi.post('/auth/refresh_token', { refreshToken });
          if (data?.token && data?.refreshToken) {
            localStorage.setItem('token', data.token);
            localStorage.setItem('refreshToken', data.refreshToken);
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            originalRequest.headers.Authorization = `Bearer ${data.token}`;
            return api(originalRequest);
          }
        } catch (_) {
          // Refresh falhou → logout e redirect
        }

        doLogout();
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );

  const doLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    clearCachedUser();
    api.defaults.headers.Authorization = undefined;
    setIsAuth(false);
    setUser({});
    const origin = localStorage.getItem('loginOrigin') || 'default';
    history.push(origin === 'mobile' ? '/mobile-login' : '/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      setLoading(false);
      if (splashTimeoutRef.current) {
        clearTimeout(splashTimeoutRef.current);
        splashTimeoutRef.current = null;
      }
    };

    splashTimeoutRef.current = setTimeout(finish, AUTH_SAFETY_TIMEOUT_MS);

    (async () => {
      if (token) {
        try {
          const { data } = await api.get('/auth/me');
          api.defaults.headers.Authorization = `Bearer ${token}`;
          setIsAuth(true);
          const resolved = await resolvePermissions(data);
          setUser(resolved);
          writeCachedUser(resolved);
        } catch (err) {
          // Backend offline: user já foi restaurado do cache no useState inicial.
          // Só exibe toast se não há dados em cache (sessão genuinamente inválida).
          if (!readCachedUser()?.id) toastError(err);
        }
      }
      finish();
    })();

    return () => {
      if (splashTimeoutRef.current) clearTimeout(splashTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      let io;
      if (!socket) {
        io = socketConnection({ user });
        setSocket(io);
      } else {
        io = socket;
      }
      const eventName = `company-${user.companyId}-user`;

      const handleUserEvent = async (data) => {
        if (data.action === 'update' && Number(data.user?.id) === Number(user.id)) {
          if (Array.isArray(data.user.permissions)) {
            // Backend incluiu permissions no evento — merge defensivo preservando trial/plan
            const incoming = data.user;
            const merged = { ...user, ...incoming };
            if (incoming.trial === undefined && user.trial !== undefined) {
              merged.trial = user.trial;
            }
            if (
              incoming.company !== undefined &&
              incoming.company?.plan === undefined &&
              user.company?.plan !== undefined
            ) {
              merged.company = { ...merged.company, plan: user.company.plan };
            }
            setUser(merged);
            writeCachedUser(merged);
          } else {
            // Backend não incluiu permissions no socket — /auth/me já traz permissions efetivas
            const fresh = await getCurrentUserInfo();
            if (fresh) {
              const resolved = await resolvePermissions({
                ...fresh,
                permissions: fresh.permissions ?? user.permissions,
                ticketWhatsappIds: fresh.ticketWhatsappIds ?? user.ticketWhatsappIds,
              });
              setUser(resolved);
              writeCachedUser(resolved);
            }
          }
        }
        if (data.action === 'permissions-updated' && Number(data.userId) === Number(user.id)) {
          const fresh = await getCurrentUserInfo();
          if (fresh) {
            // Força re-fetch de /users/:id/permissions para pegar ticketWhatsappIds atualizado.
            // Limpamos permissions para que resolvePermissions não faça early-return.
            const resolved = await resolvePermissions({
              ...fresh,
              permissions: undefined,
              ticketWhatsappIds: undefined,
            });
            setUser(resolved);
            writeCachedUser(resolved);
          }
        }
      };

      io.on(eventName, handleUserEvent);

      return () => {
        io.off(eventName, handleUserEvent);
        // io.disconnect();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [user]);

  /**
   * Fluxo pós-autenticação: usado por login e signup.
   * Recebe { token, refreshToken } e faz redirect para o app.
   */
  const handleAuthWithTokens = async (authData) => {
    if (splashTimeoutRef.current) {
      clearTimeout(splashTimeoutRef.current);
      splashTimeoutRef.current = null;
    }
    setLoading(true);

    const { token, refreshToken } = authData || {};
    if (!token) {
      setLoading(false);
      throw new Error('Resposta de autenticação inválida');
    }

    localStorage.setItem('token', token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    api.defaults.headers.Authorization = `Bearer ${token}`;

    let user = null;
    try {
      const { data: meData } = await api.get('/auth/me');
      user = await resolvePermissions(meData);
    } catch (_) {
      // /auth/me falhou; redirect e deixa o init carregar
    }

    const company = user?.company;
    if (company && has(company, 'companieSettings') && isArray(company.companieSettings?.[0])) {
      const setting = company.companieSettings[0].find((s) => s.key === 'campaignsEnabled');
      if (setting && setting.value === 'true') {
        localStorage.setItem('cshow', null);
      }
      const signSetting = company.companieSettings[0].find((s) => s.key === 'sendSignMessage');
      if (signSetting && signSetting.value === 'enabled') {
        localStorage.setItem('sendSignMessage', signSetting.value === 'enable');
      }
    }
    if (user?.profileImage) {
      localStorage.setItem('profileImage', user.profileImage);
    }

    if (user) setUser(user);
    setIsAuth(true);
    setLoginOrigin(localStorage.getItem('loginOrigin') || 'default');

    // Bloqueio e aviso de vencimento são responsabilidade do PlanPermissionsContext,
    // que lê user.trial e user.company.status vindos do /auth/me.
    // Aqui apenas redirecionamos para o app — o context decide se exibe bloqueio.
    window.location.href = '/tickets/conversation';
  };

  const handleLogin = async (userData) => {
    try {
      const { data } = await api.post('/auth/login', userData);
      await handleAuthWithTokens(data);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const origin = localStorage.getItem('loginOrigin') || 'default';

    const doRedirect = () => {
      setIsAuth(false);
      setUser({});
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('cshow');
      localStorage.removeItem('loginOrigin');
      clearCachedUser();
      api.defaults.headers.Authorization = undefined;
      setLoginOrigin('default');
      if (origin === 'mobile') {
        history.push('/mobile-login');
      } else {
        history.push('/login');
      }
    };

    // Captura o token antes de limpar — openApi não passa pelo interceptor de refresh,
    // então um 401 (token expirado) é simplesmente ignorado e o logout local prossegue
    const token = localStorage.getItem('token');
    if (token) {
      openApi
        .delete('/auth/logout', { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {});
    }
    // Limpeza de push em segundo plano (não bloqueia)
    unsubscribeAndUnregister().catch(() => {});
    // Logout e redirect logo em seguida; usuário não fica esperando API/push
    doRedirect();
  };

  const handleSetLoginOrigin = (origin = 'default') => {
    localStorage.setItem('loginOrigin', origin);
    setLoginOrigin(origin);
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get('/auth/me');
      return data;
    } catch (_) {
      return null;
    }
  };

  const refreshUser = async () => {
    const data = await getCurrentUserInfo();
    if (data) {
      const resolved = await resolvePermissions(data);
      setUser(resolved);
      writeCachedUser(resolved);
      return resolved;
    }
    return data;
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleAuthWithTokens,
    handleLogout,
    handleSetLoginOrigin,
    isMobileSession: loginOrigin === 'mobile',
    getCurrentUserInfo,
    refreshUser,
    socket,
  };
};

export default useAuth;
